import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Field, money } from "@/components/common";
import DataModule from "@/components/DataModule";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SteeringWheel, Money, Calculator, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AutoSettle() {
  const invalidate = useInvalidate();
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ driver_id: "", period_start: "", period_end: "", deductions: 0 });
  const [saving, setSaving] = useState(false);

  const run = async () => {
    if (!form.driver_id || !form.period_start || !form.period_end) { toast.error("Driver and date range are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settlements/auto-calc", {
        driver_id: form.driver_id, period_start: form.period_start, period_end: form.period_end, deductions: Number(form.deductions) || 0,
      });
      toast.success(`Settlement created · ${data.load_count} loads · net ${money(data.net_pay)}`);
      setOpen(false);
      setForm({ driver_id: "", period_start: "", period_end: "", deductions: 0 });
      invalidate(["settlements", "dashboard"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const selected = drivers.find((d) => d.id === form.driver_id);

  return (
    <div className="flex justify-start mb-3">
      <Button variant="outline" onClick={() => setOpen(true)} className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold" data-testid="auto-settle-btn">
        <Calculator size={18} weight="bold" className="mr-1.5" />Auto-Calculate Pay
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Auto-Calculate Driver Pay</DialogTitle>
            <DialogDescription className="text-slate-500">Generates a settlement from the driver's completed loads in the selected period.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Driver *">
              <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
                <SelectTrigger className="bg-white border-slate-300" data-testid="auto-settle-driver"><SelectValue placeholder="Select driver…" /></SelectTrigger>
                <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {String(d.pay_type || "").replace(/_/g, " ")} @ {d.pay_rate || 0}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {selected && <p className="text-xs text-slate-500 -mt-2">Pay basis: <span className="font-semibold capitalize">{String(selected.pay_type || "per_mile").replace(/_/g, " ")}</span> at rate {selected.pay_rate || 0}{selected.pay_type === "percentage" ? "%" : selected.pay_type === "per_mile" ? " /mi" : " /load"}</p>}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Period Start *"><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="bg-white border-slate-300" data-testid="auto-settle-start" /></Field>
              <Field label="Period End *"><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="bg-white border-slate-300" data-testid="auto-settle-end" /></Field>
            </div>
            <Field label="Deductions ($)"><Input type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} className="bg-white border-slate-300 font-mono" data-testid="auto-settle-deductions" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button onClick={run} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="auto-settle-run">{saving ? "Calculating…" : "Generate Settlement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Drivers() {
  const { user } = useAuth();
  const canWriteDrivers = ["admin", "dispatcher"].includes(user.role);
  const canWritePay = user.role === "admin";

  return (
    <div>
      <PageHeader title="Drivers & Pay" subtitle="Driver roster and settlement records" />
      <Tabs defaultValue="roster">
        <TabsList className="bg-white border border-slate-200 mb-4">
          <TabsTrigger value="roster" data-testid="tab-roster"><SteeringWheel size={16} className="mr-1.5" />Roster</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements"><Money size={16} className="mr-1.5" />Settlements</TabsTrigger>
        </TabsList>

        <TabsContent value="roster">
          <DataModule apiPath="drivers" queryKey="drivers" canWrite={canWriteDrivers} icon={SteeringWheel} testid="driver"
            canAdd={false} emptyTitle="No drivers yet" emptyHint="Drivers are added from Driver Info under each carrier."
            columns={[
              { key: "name", label: "Driver", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
              { key: "phone", label: "Phone", mono: true, hideSm: true },
              { key: "cdl_number", label: "CDL #", mono: true, hideSm: true },
              { key: "truck", label: "Truck", mono: true, hideSm: true },
              { key: "pay_type", label: "Pay", hideSm: true },
              { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "name", label: "Full Name", required: true },
              { key: "status", label: "Status", type: "select", options: ["active", "inactive"], default: "active" },
              { key: "phone", label: "Phone" }, { key: "email", label: "Email" },
              { key: "cdl_number", label: "CDL Number" }, { key: "cdl_state", label: "CDL State" },
              { key: "truck", label: "Assigned Truck #" },
              { key: "trailer", label: "Trailer #" },
              { key: "vin", label: "VIN" },
              { key: "pay_type", label: "Pay Type", type: "select", options: ["per_mile", "percentage", "salary"], default: "per_mile" },
              { key: "pay_rate", label: "Pay Rate", type: "number" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>

        <TabsContent value="settlements">
          {canWritePay && <AutoSettle />}
          <DataModule apiPath="settlements" queryKey="settlements" canWrite={canWritePay} icon={Money} testid="settlement"
            addLabel="New Settlement" emptyTitle="No settlements yet" emptyHint="Create driver pay settlements for a period."
            columns={[
              { key: "driver", label: "Driver", render: (r) => <span className="font-medium text-slate-900">{r.driver}</span> },
              { key: "period", label: "Period", hideSm: true },
              { key: "gross_pay", label: "Gross", render: (r) => <span className="font-mono text-sm text-slate-700">{money(r.gross_pay)}</span> },
              { key: "deductions", label: "Deductions", hideSm: true, render: (r) => <span className="font-mono text-sm text-red-500">-{money(r.deductions)}</span> },
              { key: "net_pay", label: "Net Pay", render: (r) => <span className="font-mono text-sm font-semibold text-emerald-600">{money(r.net_pay)}</span> },
              { key: "status", label: "Status" },
              { key: "_pdf", label: "", render: (r) => <a href={`${API}/settlements/${r.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-semibold" data-testid={`settlement-pdf-${r.id}`}><DownloadSimple size={15} />PDF</a> },
            ]}
            fields={[
              { key: "driver", label: "Driver Name", required: true },
              { key: "period", label: "Pay Period (e.g. Jun 1-7)" },
              { key: "gross_pay", label: "Gross Pay ($)", type: "number" },
              { key: "deductions", label: "Deductions ($)", type: "number" },
              { key: "net_pay", label: "Net Pay ($)", type: "number" },
              { key: "status", label: "Status", type: "select", options: ["pending", "paid"], default: "pending" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
